import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { miscs } from "./Misc.js"; // Import the misc array

const MiscDetail = () => {
  const { productSlug } = useParams(); // Get the product slug from the URL
  const misc = miscs.find((p) => p.link === `/misc/${productSlug}`);

  // Initialize the state for the current image index
  const [currentIndex, setCurrentIndex] = useState(0);

  // Hold quantity for quote
  const [quantity, setQuantity] = useState(1);

  // Hold sku for quote
  const [sku, setSku] = useState("");

  const subject = `Quote Request for ${sku}`;
  const body = `Hello,\n\nI would like to request a quote for ${quantity} units of ${sku}.\n\nThank you.`;
  const mailtoLink = `mailto:sales@containx.co?subject=${encodeURIComponent(
    subject
  )}&body=${encodeURIComponent(body)}`;

  if (!misc) {
    return <div>Product not found</div>;
  }

  // Update quantity
  const updateQuantity = (e) => {
    e.preventDefault();
    setQuantity(e.target.value);
  };

  // Update sku
  const updateSku = (e) => {
    e.preventDefault();
    setSku(e.target.value);
  };

  // Function to go to the previous image
  const prevImage = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? misc.images.length - 1 : prevIndex - 1
    );
  };

  // Function to go to the next image
  const nextImage = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === misc.images.length - 1 ? 0 : prevIndex + 1
    );
  };

  return (
    <div className="min-h-screen bg-containx-navy text-white p-4 md:p-6">
      <div className="max-w-6xl mx-auto flex flex-col lg:flex-row">
        {/* Slideshow */}
        <div className="mb-6 relative lg:w-1/2 top-10 flex flex-col items-center justify-center h-64">
          <img
            src={misc.images[currentIndex]}
            alt={misc.name}
            className="max-w-full max-h-full mx-auto object-contain mb-4" // Make image responsive
          />
          <div className="absolute top-1/2 left-2 transform -translate-y-1/2">
            <button
              onClick={prevImage}
              className="bg-gray-800 text-white p-2 rounded-l flex items-center justify-center w-10 h-10"
            >
              {/* Left Arrow Icon */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-6 h-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="4"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
          </div>
          <div className="absolute top-1/2 right-2 transform -translate-y-1/2">
            <button
              onClick={nextImage}
              className="bg-gray-800 text-white p-2 rounded-r flex items-center justify-center w-10 h-10"
            >
              {/* Right Arrow Icon */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-6 h-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="4"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Product Info */}
        <div className="lg:w-1/2 lg:pl-6">
          <h1 className="text-2xl md:text-3xl font-bold mb-4 pt-4 md:pt-2">
            {misc.name}
          </h1>
          <p className="mb-6">{misc.description}</p>
          {/* Features */}
          <h2 className="text-xl md:text-2xl font-semibold mb-2">Features</h2>
          <ul className="list-disc pl-5 mb-6">
            {misc.features.map((feature, index) => (
              <li key={index} className="mb-2">
                {feature}
              </li>
            ))}
          </ul>
          <div className="flex flex-col lg:flex-row items-center mb-6 space-y-4 lg:space-y-0 lg:space-x-4">
            {/* Quantity Input */}
            <p className="w-full lg:w-auto text-center lg:text-left">
              Request a quote for{" "}
              <input
                type="number"
                name="quantity"
                value={quantity}
                onChange={(e) => updateQuantity(e)}
                className="border rounded px-3 py-1 mr-2 w-20 lg:w-24 text-center"
              />{" "}
              boxes of{" "}
            </p>

            {/* SKU Dropdown */}
            <select
              name="sku"
              value={sku}
              onChange={(e) => updateSku(e)}
              className="border rounded px-3 py-1 w-full lg:w-auto"
            >
              <option value="" disabled>
                Select
              </option>
              {misc.specs[0].values.map((value) => (
                <option value={value} key={value}>
                  {value}
                </option>
              ))}
            </select>

            {/* Quote Button */}
            <a
              href={mailtoLink}
              className="bg-white text-containx-navy font-semibold px-4 py-2 md:px-6 md:py-3 rounded-lg shadow-lg hover:bg-containx-beige transition duration-300 w-full lg:w-auto"
            >
              Quote
            </a>
          </div>
        </div>
      </div>

      {/* Specifications Table */}
      <h2 className="text-xl md:text-2xl font-semibold mb-2">Specifications</h2>
      <div className="overflow-x-auto">
        <table className="w-full table-auto mb-6 text-left">
          <thead className="bg-containx-charcoal text-white">
            <tr>
              {misc.specs.map((spec) => (
                <th key={spec.label} className="border px-4 py-2">
                  {spec.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {misc.specs[0].values.map((_, index) => (
              <tr
                key={index}
                className={
                  index % 2 === 0 ? "bg-gray-100" : "bg-containx-charcoal"
                }
              >
                {misc.specs.map((spec) => (
                  <td key={spec.label} className="border px-4 py-2">
                    {spec.values[index]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Back Link */}
      <a
        href="/misc"
        className="text-containx-beige underline hover:text-containx-beige-dark"
      >
        Back to Misc
      </a>
    </div>
  );
};

export default MiscDetail;
